/**
 * useRenewalsBoard — TanStack Query hook for the T4 Renewals board (Story V10-J).
 *
 * live mode → GET /renewals/board via the typed client (wire shape IS the view
 *             shape — RenewalBoardItemVM only adds an optional kundeId, absent
 *             on live items → the card renders Kunde as plain text, by design).
 * mock mode → resolves MOCK_RENEWALS_BOARD (mock layer NOT deleted).
 *
 * [Source: apps/web/src/features/renewals/types.ts] (wire-swap path note)
 * [Source: docs/stories/v10-j-web-wiring-core.md AC2]
 */

import { useQuery } from '@tanstack/react-query';
import { qk } from '../../../lib/query-keys';
import { isLiveData } from '../../../lib/data-mode';
import { apiClient } from '../../../lib/api-client';
import { MOCK_RENEWALS_BOARD } from '../mock';
import type { RenewalBoardResponseDto } from '../types';
import type { RenewalBoardItemVM } from '../types';

/** Board response with the display view-model items (kundeId optional). */
export interface RenewalsBoardVM extends Omit<RenewalBoardResponseDto, 'items'> {
  items: RenewalBoardItemVM[];
}

export function useRenewalsBoard() {
  const live = isLiveData();
  return useQuery<RenewalsBoardVM>({
    queryKey: qk.renewalsBoard(live ? 'live' : 'mock'),
    queryFn: () => (live ? apiClient.renewals.board() : Promise.resolve(MOCK_RENEWALS_BOARD)),
    // mock mode: synchronous initialData (no loading flicker, deterministic tests)
    ...(live ? {} : { initialData: MOCK_RENEWALS_BOARD, staleTime: Infinity }),
  });
}
